import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import {
  authRoutes,
  routes as dashboardRoutes,
  errorRoutes,
  signUpRoutes,
} from './modules'
import { useAuthStore } from '@/modules/auth'

type TMetaAuthOptions = {
  roles?: string[]
  redirect?: string
  forbiddenRedirect?: string
}

const routes: Array<RouteRecordRaw> = [
  ...errorRoutes,
  ...signUpRoutes,
  ...authRoutes,
  ...dashboardRoutes,
]

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior() {
    return { top: 0 }
  },
  routes,
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Actualizar el título de la página
  document.title = `${import.meta.env.VITE_APP_NAME} ${to.meta?.title ? ' | ' + to.meta.title : ''}`

  // Obtener la configuración de autenticación de la ruta (puede ser boolean o TMetaAuthOptions)
  const requiresAuth = to.meta?.auth as boolean | TMetaAuthOptions

  if (!requiresAuth) {
    // Ruta pública, permitir acceso
    return next()
  }

  // Si no tenemos la información del usuario en RAM pero tenemos token
  // (e.g. F5 refresh), detenemos la navegación para validarlo primero
  if (!authStore.isAuthenticated && authStore.token) {
    await authStore.checkAuth()
  }

  // Verificar si, después de todo, seguimos sin estar autenticados
  if (!authStore.isAuthenticated) {
    const redirectOptions =
      typeof requiresAuth === 'object' ? requiresAuth.redirect : null
    const redirect = redirectOptions || '/auth/login'
    return next({ path: redirect })
  }

  // Si la ruta solo requiere estar logueado, sin validar roles
  if (typeof requiresAuth === 'boolean' && requiresAuth) {
    return next()
  }

  // Si la ruta exige roles específicos
  if (typeof requiresAuth === 'object') {
    const userRoles = authStore.userRoles
    const allowedRoles = requiresAuth.roles || []

    // Si el arreglo de permisos viene vacío, pasamos
    if (allowedRoles.length === 0) {
      return next()
    }

    const hasAccess = allowedRoles.some((role: string) =>
      userRoles.includes(role),
    )

    if (hasAccess) {
      return next()
    } else {
      const forbiddenRedirect = requiresAuth.forbiddenRedirect || '/403'
      return next({ path: forbiddenRedirect })
    }
  }

  // Permitir el acceso por defecto
  return next()
})

export default router
